import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { AiChatMessage } from '@/lib/ai-service'
import { sendChatMessage } from '@/lib/ai-service'
import { buildChatSystemPrompt } from '@/lib/ai-prompts'
import type { AiConversation, Camp } from '@/types/database'
import type { CampData } from '@/store/reducer'
import { apiList, apiInsert, apiUpdate } from '@/lib/api-client'

interface UseAiChatOptions {
  camp: Camp
  campData: CampData
  activeTab?: string
}

export function useAiChat({ camp, campData, activeTab }: UseAiChatOptions) {
  const { t } = useTranslation()
  const [messages, setMessages] = useState<AiChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const conversationIdRef = useRef<string | null>(null)

  // Load existing conversation for this trip: list ai_conversations scoped to
  // this camp (server-side filter) and pick the most recent row.
  useEffect(() => {
    let cancelled = false
    async function loadConversation() {
      try {
        const rows = await apiList<AiConversation>('ai_conversations', { camp_id: camp.id })
        const latest = rows
          .sort((a, b) => b.updated_at.localeCompare(a.updated_at))[0]
        if (!cancelled && latest) {
          conversationIdRef.current = latest.id
          setMessages(latest.messages ?? [])
        }
      } catch {
        /* best-effort: an empty conversation is fine */
      }
    }
    loadConversation()
    return () => { cancelled = true }
  }, [camp.id])

  // Persist conversation through the generic CRUD proxy.
  const persistConversation = useCallback(async (msgs: AiChatMessage[]) => {
    if (conversationIdRef.current) {
      await apiUpdate('ai_conversations', conversationIdRef.current, {
        messages: msgs,
        updated_at: new Date().toISOString(),
      })
    } else {
      const created = await apiInsert<AiConversation>('ai_conversations', {
        camp_id: camp.id,
        messages: msgs,
        updated_at: new Date().toISOString(),
      })
      if (created?.id) {
        conversationIdRef.current = created.id
      }
    }
  }, [camp.id])

  const sendMessage = useCallback(async (content: string) => {
    setError(null)
    const userMessage: AiChatMessage = { role: 'user', content }
    const updatedMessages = [...messages, userMessage]
    setMessages(updatedMessages)
    setIsLoading(true)

    try {
      const systemPrompt = buildChatSystemPrompt(camp, campData, activeTab)
      const reply = await sendChatMessage(updatedMessages, systemPrompt)
      const assistantMessage: AiChatMessage = { role: 'assistant', content: reply }
      const finalMessages = [...updatedMessages, assistantMessage]
      setMessages(finalMessages)
      await persistConversation(finalMessages)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('aiChat.responseFailed'))
    } finally {
      setIsLoading(false)
    }
  }, [messages, camp, campData, activeTab, persistConversation, t])

  const clearChat = useCallback(async () => {
    setMessages([])
    setError(null)
    conversationIdRef.current = null
  }, [])

  return { messages, isLoading, error, sendMessage, clearChat }
}
